import type { Branch } from '../auth/types';
import type { BusinessReport, CatalogSnapshot, OperationsSnapshot, Phase8Snapshot, ReportPeriod, SalesSnapshot } from '../../types/domain';

const inPeriod = (iso: string, period: ReportPeriod) => {
  const date = iso.slice(0, 10);
  return date >= period.from && date <= period.to;
};

export const defaultReportPeriod = (): ReportPeriod => {
  const date = new Date();
  const to = date.toISOString().slice(0, 10);
  const fromDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 12));
  return { from: fromDate.toISOString().slice(0, 10), to };
};

export function buildBusinessReport(input: {
  period: ReportPeriod;
  branches: Branch[];
  sales: SalesSnapshot;
  catalog: CatalogSnapshot;
  operations: OperationsSnapshot;
  phase8: Phase8Snapshot;
  branchId?: string;
}): BusinessReport {
  const { period, branches, sales, catalog, operations, phase8, branchId } = input;
  const branchFilter = (value: string) => !branchId || value === branchId;
  const periodSales = sales.sales.filter((sale) => branchFilter(sale.branchId) && inPeriod(sale.createdAt, period) && sale.status !== 'cancelled');
  const periodExpenses = operations.expenses.filter((expense) => branchFilter(expense.branchId) && expense.status === 'recorded' && inPeriod(expense.expenseDate, period));
  const periodPurchases = operations.purchases.filter((purchase) => branchFilter(purchase.branchId) && purchase.status !== 'cancelled' && inPeriod(purchase.purchasedAt, period));
  const salesCents = periodSales.reduce((sum, sale) => sum + sale.totalCents, 0);
  const collectedCents = periodSales.reduce((sum, sale) => sum + Math.min(sale.paidCents, sale.totalCents), 0);
  const outstandingCents = periodSales.reduce((sum, sale) => sum + sale.balanceCents, 0);
  const expenseCents = periodExpenses.reduce((sum, expense) => sum + expense.amountCents, 0);
  const purchaseCents = periodPurchases.reduce((sum, purchase) => sum + purchase.totalCents, 0);
  let grossProfitCents = 0;
  let missingCostItems = 0;
  const productMap = new Map<string, { productId: string; name: string; category: string; quantity: number; revenueCents: number; estimatedProfitCents: number; missingCost: boolean }>();
  for (const sale of periodSales) {
    for (const item of sale.items) {
      const revenue = item.unitPriceCents * item.quantity;
      const missing = item.costCents <= 0 && item.supplyType !== 'service';
      const profit = missing ? 0 : (item.unitPriceCents - item.costCents) * item.quantity;
      if (missing) missingCostItems += 1;
      grossProfitCents += profit;
      const product = catalog.products.find((entry) => entry.id === item.productId);
      const current = productMap.get(item.productId) ?? { productId: item.productId, name: item.name, category: product?.category ?? 'Sin categoría', quantity: 0, revenueCents: 0, estimatedProfitCents: 0, missingCost: false };
      current.quantity += item.quantity;
      current.revenueCents += revenue;
      current.estimatedProfitCents += profit;
      current.missingCost = current.missingCost || missing;
      productMap.set(item.productId, current);
    }
  }
  const branchPerformance = branches.filter((branch) => !branchId || branch.id === branchId).map((branch) => {
    const branchSales = periodSales.filter((sale) => sale.branchId === branch.id);
    const branchExpenses = periodExpenses.filter((expense) => expense.branchId === branch.id).reduce((sum, expense) => sum + expense.amountCents, 0);
    const revenue = branchSales.reduce((sum, sale) => sum + sale.totalCents, 0);
    const cost = branchSales.reduce((sum, sale) => sum + sale.items.reduce((itemSum, item) => itemSum + (item.costCents > 0 ? item.costCents * item.quantity : 0), 0), 0);
    return { branchId: branch.id, branchName: branch.name, salesCents: revenue, expenseCents: branchExpenses, estimatedProfitCents: revenue - cost - branchExpenses, tickets: branchSales.length, averageTicketCents: branchSales.length ? Math.round(revenue / branchSales.length) : 0 };
  });
  const paymentMethods: Record<string, number> = {};
  const channels: Record<string, number> = {};
  for (const sale of periodSales) {
    channels[sale.channel] = (channels[sale.channel] ?? 0) + sale.totalCents;
    for (const payment of sale.payments) paymentMethods[payment.method] = (paymentMethods[payment.method] ?? 0) + payment.amountCents;
  }
  const activeMemberships = phase8.memberships.filter((membership) => membership.status === 'active').length;
  const recurringRevenueCents = phase8.memberships.filter((membership) => membership.status === 'active').reduce((sum, membership) => sum + (phase8.membershipPlans.find((plan) => plan.id === membership.planId)?.priceCents ?? 0), 0);
  const overdueMembershipCents = phase8.membershipCharges.filter((charge) => charge.status === 'overdue').reduce((sum, charge) => sum + charge.amountCents - charge.paidCents, 0);
  const lowStockCount = catalog.products.filter((product) => product.trackInventory).filter((product) => {
    const rows = catalog.inventory.filter((row) => row.productId === product.id && (!branchId || row.branchId === branchId));
    const available = rows.reduce((sum, row) => sum + row.onHand - row.reserved, 0);
    return available <= product.minStock;
  }).length;
  const pendingOrders = sales.publicOrders.filter((order) => branchFilter(order.branchId) && !['completed', 'cancelled'].includes(order.status)).length + sales.sales.filter((sale) => branchFilter(sale.branchId) && ['reserved', 'pending-order', 'ready', 'partially-delivered'].includes(sale.status)).length;
  return {
    period,
    salesCents,
    collectedCents,
    outstandingCents,
    expenseCents,
    purchaseCents,
    estimatedProfitCents: grossProfitCents - expenseCents,
    missingCostItems,
    tickets: periodSales.length,
    averageTicketCents: periodSales.length ? Math.round(salesCents / periodSales.length) : 0,
    newCustomers: sales.customers.filter((customer) => inPeriod(customer.createdAt, period)).length,
    activeMemberships,
    recurringRevenueCents,
    overdueMembershipCents,
    lowStockCount,
    pendingOrders,
    branches: branchPerformance.sort((a, b) => b.salesCents - a.salesCents),
    products: [...productMap.values()].sort((a, b) => b.revenueCents - a.revenueCents),
    paymentMethods,
    channels,
  };
}

const csvCell = (value: string | number) => `"${String(value).replace(/"/g, '""')}"`;
export const businessReportCsv = (report: BusinessReport) => {
  const rows: (string | number)[][] = [
    ['Reporte NEXO COMERCIO', `${report.period.from} a ${report.period.to}`],
    ['Ventas', report.salesCents / 100],
    ['Cobrado', report.collectedCents / 100],
    ['Saldo pendiente', report.outstandingCents / 100],
    ['Gastos', report.expenseCents / 100],
    ['Utilidad estimada', report.estimatedProfitCents / 100],
    [],
    ['Producto', 'Categoría', 'Cantidad', 'Ingreso', 'Utilidad estimada', 'Costo faltante'],
    ...report.products.map((product) => [product.name, product.category, product.quantity, product.revenueCents / 100, product.estimatedProfitCents / 100, product.missingCost ? 'Sí' : 'No']),
    [],
    ['Sucursal', 'Ventas', 'Gastos', 'Resultado estimado', 'Tickets', 'Ticket promedio'],
    ...report.branches.map((branch) => [branch.branchName, branch.salesCents / 100, branch.expenseCents / 100, branch.estimatedProfitCents / 100, branch.tickets, branch.averageTicketCents / 100]),
  ];
  return rows.map((row) => row.map(csvCell).join(',')).join('\n');
};

const latin = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^\x20-\x7E]/g, '?').replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
export const simpleReportPdf = (title: string, lines: string[]) => {
  const content = [`BT /F1 16 Tf 52 790 Td (${latin(title)}) Tj`, '/F1 10 Tf 0 -24 Td'];
  for (const line of lines.slice(0, 45)) content.push(`(${latin(line)}) Tj 0 -16 Td`);
  content.push('ET');
  const stream = content.join('\n');
  const objects = [
    '1 0 obj << /Type /Catalog /Pages 2 0 R >> endobj',
    '2 0 obj << /Type /Pages /Kids [3 0 R] /Count 1 >> endobj',
    '3 0 obj << /Type /Page /Parent 2 0 R /MediaBox [0 0 612 842] /Resources << /Font << /F1 5 0 R >> >> /Contents 4 0 R >> endobj',
    `4 0 obj << /Length ${stream.length} >> stream\n${stream}\nendstream endobj`,
    '5 0 obj << /Type /Font /Subtype /Type1 /BaseFont /Helvetica >> endobj',
  ];
  let pdf = '%PDF-1.4\n';
  const offsets = [0];
  for (const object of objects) { offsets.push(pdf.length); pdf += `${object}\n`; }
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  for (let index = 1; index <= objects.length; index += 1) pdf += `${String(offsets[index]).padStart(10, '0')} 00000 n \n`;
  pdf += `trailer << /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  return new Blob([pdf], { type: 'application/pdf' });
};

export const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url; anchor.download = filename; anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
};
